import React from 'react';
import { CreateEventVariables, CreateEvent, CurrentUser_me, CurrentUser, CurrentUser_me_aliases } from '@graphql/types';
import { Spinner } from '..';
import { JoyHeader } from '@components/JoyHeader';
import { MutationFn } from 'react-apollo';
import { getLocalStorage, TrackingFunc, TrackErrorFunc } from '@withjoy/web-core';
import { ErrorPage } from '@components/Error';
import { ApolloError } from 'apollo-client';
import { buildReactStateHandler } from '@lib';
import { withRouter, RouteComponentProps } from 'react-router';
import { Text } from '@withjoy/joykit';
import { Container, FlexCenterWrapper } from './styles';
import { Questionnaire } from '../../../Questionnaire';
import { DEFAULT_STATE } from './defaultState';
import { FetchResult } from 'apollo-link';

const stateUpdater = buildReactStateHandler<CreateEventState>();
const weddingStorage = getLocalStorage();

interface IdentifyProp {
  userId?: string;
  firstName?: string;
  lastName?: string;
  eventId?: string;
}

type Option = {
  label: string;
  checked: boolean;
  name: string;
  telemetryName: string;
};

interface CreateEventProps extends RouteComponentProps {
  createEvent: MutationFn<CreateEvent, CreateEventVariables>;
  currentUser?: CurrentUser;
  refetch: () => void;
  track: TrackingFunc;
  trackError: TrackErrorFunc;
  identify: (args: IdentifyProp) => void;
}

export interface CreateEventState {
  error: ApolloError | null;
  createEventResponse: void | FetchResult<CreateEvent, Record<string, any>, Record<string, any>>;
  createEventComplete: boolean;
  showSurvey: boolean;
  options: Option[];
  formError: boolean;
}

const reverseFontMapping = {
  Allura: 'Lush Script',
  'Pinyon Script': 'Penna Connected',
  'Fjalla One': 'Komu',
  Roboto: 'Proxima Nova'
};

export class CreateEventMutationComponent extends React.PureComponent<CreateEventProps, CreateEventState> {
  readonly state = DEFAULT_STATE;

  hasExistingEvent = (currentUser: CurrentUser_me) =>
    currentUser &&
    currentUser.eventUsers &&
    currentUser.eventUsers.reduce((acc, curr) => (curr && curr.status === 'owner' ? curr : acc), null as any);

  async componentDidMount() {
    if (!this.props.currentUser || !this.props.currentUser.me) {
      this.props.history.push('/createwedding');
    } else if (this.hasExistingEvent(this.props.currentUser.me)) {
      this.props.history.push('/createwedding');
    } else {
      const eventData = weddingStorage.getItem('createEventJson');
      if (eventData) {
        const parsedData = JSON.parse(eventData);
        if (parsedData) {
          const {
            ownerFirstName,
            ownerLastName,
            fianceeFirstName,
            fianceeLastName,
            lat,
            lng,
            date,
            calendarDate,
            enableTranslucentEffect,
            font,
            place,
            placeId,
            template,
            theme,
            useSuggestedBackgroundColor,
            useWeddingColor,
            graphicAccent,
            tzid
          } = parsedData;

          let paletteId = null;
          let paletteName = null;
          let accentColor = null;
          let baseColor = null;
          let accentText = null;
          let baseText = null;
          let accentLightText = false;
          let baseLightText = false;

          const { palette } = parsedData;
          if (palette) {
            paletteId = palette.paletteId;
            paletteName = palette.paletteName;
            accentColor = palette.accentColor;
            baseText = palette.baseText;
            accentLightText = palette.accentLightText;
            baseLightText = palette.baseLightText;
          }

          const variables = {
            variables: {
              ownerFirstName,
              ownerLastName,
              fianceeFirstName,
              fianceeLastName,
              date,
              calendarDate,
              place: place === '' ? null : place,
              placeId,
              lat,
              lng,
              template,
              theme,
              font: reverseFontMapping[font] ? reverseFontMapping[font] : font,
              graphicAccent,
              enableTranslucentEffect,
              useSuggestedBackgroundColor,
              useWeddingColor,
              paletteId,
              paletteName,
              accentColor,
              baseColor,
              accentText,
              baseText,
              accentLightText,
              baseLightText,
              tzid
            }
          };
          try {
            const aliases = this.props.currentUser.me.aliases;
            const latestTimestamp = Math.max.apply(
              null,
              aliases.map(function(e: CurrentUser_me_aliases) {
                return +e.createdAt;
              })
            );

            const latestAccount = aliases.find(alias => alias.createdAt === String(latestTimestamp));
            const auth0Id = latestAccount && latestAccount.auth0 ? latestAccount.auth0 : '';
            this.props.identify({ userId: auth0Id });

            const value = await this.props.createEvent(variables);
            this.setState(
              stateUpdater(draft => {
                draft.createEventResponse = value;
                draft.createEventComplete = true;
              })
            );
          } catch (e) {
            this.setState(
              stateUpdater(draft => {
                draft.error = e;
              })
            );
          }
        }
      }
    }
  }

  canSubmit = () => !this.state.options.every(value => value.checked === false);

  getResponses = () =>
    this.state.options.reduce((responses, currentValue) => {
      responses[currentValue.telemetryName] = currentValue.checked;
      return responses;
    }, {});

  finishCreateWedding = (response: FetchResult<CreateEvent, Record<string, any>, Record<string, any>>) => {
    const uniqueAffiliateTransactionID = weddingStorage.getItem('uniqueAffiliateTransactionID');
    const affiliateName = weddingStorage.getItem('affiliateName');

    if (response && response.data && response.data.createEvent && response.data.createEvent.website) {
      const eventId = response.data.createEvent.eventId || null;
      const responseObj = this.getResponses();

      this.props.track({
        action: 'Publish',
        label: response.data.createEvent.website,
        extraInfo: { uniqueAffiliateTransactionID, joyAffiliateName: affiliateName, eventId, ...responseObj }
      });
      window.location.href = `${window.config().host}/${response.data.createEvent.website}/edit`;
    } else {
      window.location.href = `${window.config().host}/me`;
    }
    weddingStorage.setItem('createEventJson', null);
  };

  onSkip = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const responseObj = this.getResponses();

    this.props.track({
      actionType: 'click',
      action: 'HearAboutJoySkip',
      extraInfo: { ...responseObj }
    });

    this.setState(
      stateUpdater(draft => {
        draft.showSurvey = false;
      })
    );
  };
  surveryComplete = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (this.canSubmit()) {
      const responseObj = this.getResponses();

      this.props.track({
        actionType: 'click',
        action: 'HearAboutJoySubmit',
        extraInfo: { ...responseObj }
      });

      this.setState(
        stateUpdater(draft => {
          draft.showSurvey = false;
        })
      );
    } else {
      this.props.trackError({
        attemptedAction: 'submit',
        errorDescription:
          'User pressed submit without selecting an option. They should either press skip, or submit with an option selected.',
        error: 'HearAboutJoyValidation'
      });

      this.setState(
        stateUpdater(draft => {
          draft.formError = true;
        })
      );
    }
  };

  handleFormInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.checked;
    const field = event.currentTarget.dataset.field;
    const label = event.currentTarget.dataset.label;
    const resultingState = value ? 'selected' : 'unselected';

    this.props.track({
      actionType: 'click',
      action: 'HearAboutJoyOption',
      label,
      extraInfo: { resultingState }
    });

    this.setState(
      stateUpdater(draft => {
        draft.options = draft.options.map(option => {
          if (option.name === field) {
            option.checked = value;
          }
          return option;
        });

        if (draft.formError) {
          draft.formError = false;
        }
      })
    );
  };

  renderComponent = () => {
    if (this.state.createEventComplete && !this.state.showSurvey) {
      this.finishCreateWedding(this.state.createEventResponse!);
      return;
    } else if (!this.state.createEventComplete && !this.state.showSurvey) {
      return (
        <Container spacing="xs">
          <Text component="h1" textAlign="center" variant="page">
            We’re Tying the Knot 💍
          </Text>
          <Text component="h2" variant="title" textAlign="center">
            Please wait as we build your website
          </Text>
          <Spinner />
        </Container>
      );
    } else if (this.state.showSurvey) {
      return (
        <Questionnaire
          track={this.props.track}
          onSubmit={this.surveryComplete}
          onChange={this.handleFormInput}
          options={this.state.options}
          error={this.state.formError}
          onSkip={this.onSkip}
          errorText="Please select an option or press skip"
        />
      );
    }

    return <Spinner />;
  };

  render() {
    if (this.state.error) {
      return (
        <ErrorPage
          page="tyingTheKnot"
          pagePrefix="marketing"
          category="CreateWedding"
          attemptedAction="Create Event Mutation"
          error={this.state.error!}
        />
      );
    }

    return (
      <div style={{ padding: '40px' }}>
        <JoyHeader>
          <JoyHeader.Left />
        </JoyHeader>
        <FlexCenterWrapper>
          <div style={{ paddingTop: '22px', paddingLeft: '22px' }} />
          {this.renderComponent()}
          <div style={{ margin: '33px 0' }} />
        </FlexCenterWrapper>
      </div>
    );
  }
}

export const CreateEventMutation = withRouter(CreateEventMutationComponent);
